import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import { type AggregationRoot, MutableEntity } from '../common';

import Project = AuthDomain.Object.Project;

import { dayjs } from '../../../shared/utils/date';

export type MetadataField = AggregationRoot &
  MutableEntity & {
    key: MetadataField.Key;
    name: string;
    dataType: MetadataField.DataType;
  };

export namespace MetadataField {
  export const DataType = {
    String: 'string',
    Number: 'number',
    Boolean: 'boolean',
  } as const;
  export type DataType = (typeof DataType)[keyof typeof DataType];

  export type Key = Tagged<string, 'MetadataFieldKey'>;

  export namespace Key {
    export function FromString(key: string): Key {
      if (key.length === 0) throw new Error('MetadataField key must not be empty');
      return key as Key;
    }
  }

  type ConstructProps = MutableEntity.Props & {
    projectId: string;
    key: string;
    name: string;
    dataType: string;
  };
  export const Construct = (props: ConstructProps): MetadataField => {
    return {
      projectId: Project.Id.FromString(props.projectId),
      key: Key.FromString(props.key),
      name: props.name,
      dataType: props.dataType as DataType,
      ...MutableEntity.Construct(props),
    };
  };

  export namespace Create {
    export type Params = {
      props: Pick<MetadataField, 'projectId' | 'key' | 'name' | 'dataType'>;
      user: string;
    };
  }
  export const Create = (params: Create.Params): MetadataField => {
    const { props, user } = params;
    const date = dayjs(new Date());
    return {
      projectId: props.projectId,
      key: props.key,
      name: props.name,
      dataType: props.dataType,
      createdAt: date,
      createdBy: user,
      updatedAt: date,
      updatedBy: user,
    };
  };

  export namespace Update {
    export type Params = {
      current: MetadataField;
      props: Partial<Pick<MetadataField, 'name' | 'dataType'>>;
      user: string;
    };
  }
  export const Update = (params: Update.Params): MetadataField => {
    const { current, props, user } = params;
    return {
      ...current,
      name: props.name ?? current.name,
      dataType: props.dataType ?? current.dataType,
      ...MutableEntity.Update({ cur: current, user }),
    };
  };
}
